import React, { ChangeEvent, ReactNode, useCallback } from 'react';
import DataType from '../../types/data.type'

import { Checkbox, Text } from 'components'
import { get } from 'lodash'
import { DEFAULT_EMPTY_MSG, DEFAULT_INITIAL_SEARCH_MSG } from 'shared/components/list/constants'
import { classesBuilder } from 'shared/utils'
import * as style from 'theme/components/webform/select/select.module.scss'
import Grid from '../../../../../shared/components/context/grid'

export type ColumnSelectOption = {
  title: string
  path: string
  custom?: (option: DataType) => ReactNode
}

type BaseProps = {
  data?: DataType[] | null
  selecteds?: DataType[] | null
  onClick?: Function
  multiple?: boolean
  customOption?: (option: DataType, isSelected: boolean) => ReactNode
  columns?: ColumnSelectOption[]
  isRequest?: boolean
  requestFinished?: boolean
  customMessageNotFound?: string
  customMessageInitial?: string
}

const Base: React.FC<BaseProps> = ({
  data,
  onClick,
  selecteds,
  multiple,
  customOption,
  columns,
  isRequest,
  requestFinished,
  customMessageNotFound,
  customMessageInitial,
}) => {
  const isSelected = useCallback(
    (line: DataType): boolean =>
      selecteds?.some((selected) => selected.value === line.value) ?? false,
    [selecteds],
  )

  const handleClick = (line: DataType, event: ChangeEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    onClick?.(line);
  }

  const hasClickAction = (line: DataType): { onClick: Function } | null => {
    if (onClick && line.disabled !== true) {
      return { onClick: (event: ChangeEvent<HTMLDivElement>) => handleClick(line, event) }
    }
    return null
  }

  return (
    <>
      {data && data.length === 0 && (
        <div className={classesBuilder(style, { list: true })}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className={classesBuilder(style, { line: true })}>
              <Text value="" rounded />
            </div>
          ))}
        </div>
      )}

      {data?.length && !columns?.length
        ? data.map((line) => (
            <div
              key={line.value}
              className={classesBuilder(style, {
                line: true,
                selected: isSelected(line),
                ...(line.disabled ? { disabled: true } : null),
              })}
              title={line.title}
              {...(hasClickAction(line))}
            >
              {multiple && (
                <div className={classesBuilder(style, { checkbox: true })}>
                  <Checkbox
                    small
                    checked={isSelected(line)}
                    disabled={line.disabled}
                  />
                </div>
              )}
              {customOption ? customOption(line, isSelected(line)) : line.text}
            </div>
          ))
        : null}

      {data?.length && columns?.length ? (
        <>
          {!!data.length && (
            <div className={classesBuilder(style, { header: true })}>
              <div className={classesBuilder(style, { line: true })}>
                {multiple && (
                  <div className={classesBuilder(style, { cell: true })}>
                    <div className={classesBuilder(style, { checkbox: true })}></div>
                  </div>
                )}
                {columns.map((column) => (
                  <div key={column.title} className={classesBuilder(style, { cell: true })}>
                    <Grid alignItems="center">{column.title}</Grid>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!data.length && (
            <div className={classesBuilder(style, { body: true })}>
              {(data || []).map((line, indexLine) => (
                <div
                  key={`line_${indexLine}`}
                  className={classesBuilder(style, {
                    line: true,
                    selected: isSelected(line),
                  })}
                  {...(hasClickAction(line))}
                >
                  {multiple && (
                    <div className={classesBuilder(style, { cell: true })}>
                      <div className={classesBuilder(style, { checkbox: true })}>
                        <Checkbox
                          small
                          checked={isSelected(line)}
                          disabled={line.disabled}
                        />
                      </div>
                    </div>
                  )}
                  {columns.map((column, indexCell) => (
                    <div key={`line_${indexLine}_cell_${indexCell}`} className={classesBuilder(style, { cell: true })}>
                      {column.custom?.(line) || (column.path && get(line, column.path, ''))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}

      {!data && isRequest ? (
        <>
          {requestFinished ? (
            <div className={classesBuilder(style, { empty: true })}>
              <Text
                size="medium"
                icon="alert-triangle-outline"
                iconColor="warning"
                value={customMessageNotFound || DEFAULT_EMPTY_MSG}
              />
            </div>
          ) : (
            <div className={classesBuilder(style, { empty: true })}>
              <Text
                size="medium"
                icon="alert-triangle-outline"
                iconColor="warning"
                value={customMessageInitial || DEFAULT_INITIAL_SEARCH_MSG}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {!data && (
            <div className={classesBuilder(style, { empty: true })}>
              <Text
                size="medium"
                icon="alert-triangle-outline"
                iconColor="warning"
                value={customMessageNotFound || DEFAULT_EMPTY_MSG}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}

export default Base
